import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@supabase/supabase-js';
import {
  IconSearch,
  IconHome,
  IconStar,
  IconFolder,
  IconNote,
  IconPlus,
  IconClose,
  IconEdit,
  IconTrash,
  IconCopy,
  IconUpload,
  IconDownload,
  IconMove,
  IconLock,
} from './Icons';
import { useAuth } from '../contexts/AuthContext';
import './Files.css';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const noOpLock = async (_name, _acquireTimeout, fn) => fn();
const filesSupabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: true, autoRefreshToken: true, detectSessionInUrl: true, lock: noOpLock },
});

const FETCH_TIMEOUT_MS = 12000;
const OFFICIAL_DOC_TEMPLATE_URL = process.env.PUBLIC_URL + '/official-document.docx';

const FM_BUCKET = 'fm-files';
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB
const SIGNED_URL_EXPIRY_SEC = 60;

async function getSignedDownloadUrl(client, storageKey) {
  const { data, error } = await client.storage.from(FM_BUCKET).createSignedUrl(storageKey, SIGNED_URL_EXPIRY_SEC);
  if (error) throw error;
  return data?.signedUrl;
}

function formatSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, { dateStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function Files() {
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const [downloadingOfficialDoc, setDownloadingOfficialDoc] = useState(false);
  const [search, setSearch] = useState('');
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [breadcrumb, setBreadcrumb] = useState([{ id: null, name: 'Home' }]);
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [favoritedFolders, setFavoritedFolders] = useState([]);
  const [favoritedFiles, setFavoritedFiles] = useState([]);
  const [favoritesView, setFavoritesView] = useState(false);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderAccess, setNewFolderAccess] = useState([]);

  const [showUpload, setShowUpload] = useState(false);
  const [uploadAccess, setUploadAccess] = useState([]);
  const [uploadError, setUploadError] = useState(null);

  const [showRename, setShowRename] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameValue, setRenameValue] = useState('');

  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [showAccess, setShowAccess] = useState(false);
  const [accessTarget, setAccessTarget] = useState(null);
  const [accessList, setAccessList] = useState([]);
  const [accessAddUserId, setAccessAddUserId] = useState('');

  const [showMove, setShowMove] = useState(false);
  const [moveTarget, setMoveTarget] = useState(null);
  const [moveDestinationId, setMoveDestinationId] = useState(null);
  const [moveFolderOptions, setMoveFolderOptions] = useState([]);

  const mountedRef = useRef(true);

  const runWakeUp = useCallback(() => {
    return Promise.race([
      filesSupabase.from('profiles').select('id').limit(1),
      new Promise((_, reject) => setTimeout(() => reject(new Error('wakeup')), 5000)),
    ]).catch(() => {});
  }, []);

  const fetchFoldersAndFiles = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const folderId = currentFolderId;
      let foldersQuery = filesSupabase.from('fm_folders').select('*');
      let filesQuery = filesSupabase.from('fm_files').select('*');
      if (folderId == null) {
        foldersQuery = foldersQuery.is('parent_id', null);
        filesQuery = filesQuery.is('folder_id', null);
      } else {
        foldersQuery = foldersQuery.eq('parent_id', folderId);
        filesQuery = filesQuery.eq('folder_id', folderId);
      }
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timed out. Try refreshing.')), FETCH_TIMEOUT_MS)
      );
      const [foldersRes, filesRes] = await Promise.all([
        Promise.race([foldersQuery.order('name'), timeoutPromise]),
        Promise.race([filesQuery.order('name'), timeoutPromise]),
      ]);
      const { data: foldersData, error: foldersErr } = foldersRes;
      const { data: filesData, error: filesErr } = filesRes;
      if (foldersErr) setError(foldersErr.message);
      else setFolders(foldersData ?? []);
      if (filesErr) setError(filesErr.message);
      else setFiles(filesData ?? []);
    } catch (e) {
      setError(e?.message || 'Failed to load.');
      setFolders([]);
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [currentFolderId]);

  const fetchFavorites = useCallback(async () => {
    if (!user?.id) return;
    const { data, error: err } = await filesSupabase
      .from('fm_favorites')
      .select('*')
      .eq('user_id', user.id);
    if (!err) setFavorites(data ?? []);
  }, [user?.id]);

  const fetchProfiles = useCallback(async () => {
    const { data, err } = await filesSupabase
      .from('profiles')
      .select('id, full_name')
      .order('full_name', { ascending: true, nullsFirst: false });
    if (!err) setProfiles(data ?? []);
  }, []);

  const refreshSessionAndFetch = useCallback(async () => {
    mountedRef.current = true;
    setError(null);
    try {
      await filesSupabase.auth.getSession();
      await runWakeUp();
      if (favoritesView) {
        await fetchFavorites();
      } else {
        await fetchFoldersAndFiles();
      }
      await fetchProfiles();
    } catch (e) {
      setError(e?.message || 'Failed to load');
    }
  }, [favoritesView, runWakeUp, fetchFoldersAndFiles, fetchFavorites, fetchProfiles]);

  useEffect(() => {
    let cancelled = false;
    mountedRef.current = true;
    if (favoritesView) {
      setFolders([]);
      setFiles([]);
      setLoading(false);
      (async () => {
        await filesSupabase.auth.getSession();
        if (cancelled) return;
        await runWakeUp();
        if (cancelled) return;
        await fetchFavorites();
        if (cancelled) return;
        await fetchProfiles();
      })();
    } else {
      (async () => {
        const { error: sessionErr } = await filesSupabase.auth.getSession();
        if (cancelled) return;
        if (sessionErr) {
          setError(sessionErr.message);
          setLoading(false);
          return;
        }
        await runWakeUp();
        if (cancelled) return;
        await fetchFoldersAndFiles();
      })();
    }
    return () => { cancelled = true; mountedRef.current = false; };
  }, [favoritesView, runWakeUp, fetchFoldersAndFiles, fetchFavorites, fetchProfiles]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      refreshSessionAndFetch();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshSessionAndFetch]);

  useEffect(() => {
    if (!favoritesView || !favorites.length) {
      setFavoritedFolders([]);
      setFavoritedFiles([]);
      return;
    }
    const folderIds = [...new Set(favorites.filter((f) => f.folder_id).map((f) => f.folder_id))];
    const fileIds = [...new Set(favorites.filter((f) => f.file_id).map((f) => f.file_id))];
    if (folderIds.length === 0 && fileIds.length === 0) {
      setFavoritedFolders([]);
      setFavoritedFiles([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const [foldersRes, filesRes] = await Promise.all([
        folderIds.length ? filesSupabase.from('fm_folders').select('*').in('id', folderIds) : { data: [] },
        fileIds.length ? filesSupabase.from('fm_files').select('*').in('id', fileIds) : { data: [] },
      ]);
      if (!cancelled) {
        setFavoritedFolders(foldersRes.data ?? []);
        setFavoritedFiles(filesRes.data ?? []);
      }
    })();
    return () => { cancelled = true; };
  }, [favoritesView, favorites]);

  useEffect(() => {
    fetchFavorites();
    fetchProfiles();
  }, [fetchFavorites, fetchProfiles]);

  const navigateToFolder = (folder) => {
    if (!folder) {
      setCurrentFolderId(null);
      setBreadcrumb([{ id: null, name: 'Home' }]);
    } else {
      setCurrentFolderId(folder.id);
      setBreadcrumb((prev) => {
        const i = prev.findIndex((b) => b.id === folder.id);
        if (i >= 0) return prev.slice(0, i + 1);
        return [...prev, { id: folder.id, name: folder.name }];
      });
    }
    setSelectedItem(null);
    setFavoritesView(false);
  };

  const navigateBreadcrumb = (item) => {
    setCurrentFolderId(item.id);
    setBreadcrumb((prev) => {
      const i = prev.findIndex((b) => b.id === item.id);
      return prev.slice(0, i + 1);
    });
    setSelectedItem(null);
    setFavoritesView(false);
  };

  const isFavorited = (type, id) =>
    favorites.some((f) => (type === 'file' && f.file_id === id) || (type === 'folder' && f.folder_id === id));

  const openNewFolderModal = () => {
    setNewFolderName('');
    setNewFolderAccess([]);
    setShowNewFolder(true);
  };

  const createFolder = async (e) => {
    e.preventDefault();
    if (!newFolderName.trim() || !user?.id) return;
    setSaving(true);
    setError(null);
    await runWakeUp();
    const { data: folder, error: err } = await filesSupabase
      .from('fm_folders')
      .insert({
        parent_id: currentFolderId,
        name: newFolderName.trim(),
      })
      .select('id')
      .single();
    if (err) {
      setError(err.message);
      setSaving(false);
      return;
    }
    for (const uid of newFolderAccess) {
      await filesSupabase.from('fm_folder_access').insert({ folder_id: folder.id, user_id: uid });
    }
    setShowNewFolder(false);
    setSaving(false);
    fetchFoldersAndFiles();
  };

  const openUploadModal = () => {
    setUploadAccess([]);
    setUploadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowUpload(true);
  };

  const handleFileSelect = async (e) => {
    const selected = e.target.files;
    if (!selected?.length || !user?.id) return;
    setUploadError(null);
    const toUpload = [];
    for (let i = 0; i < selected.length; i++) {
      const file = selected[i];
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setUploadError(`"${file.name}" exceeds 5 MB. Skipped.`);
        continue;
      }
      toUpload.push(file);
    }
    if (toUpload.length === 0) return;
    setSaving(true);
    await runWakeUp();
    for (const file of toUpload) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storageKey = `${user.id}/${Date.now()}_${safeName}`;
      const { error: uploadErr } = await filesSupabase.storage.from(FM_BUCKET).upload(storageKey, file, { upsert: false });
      if (uploadErr) {
        setUploadError(uploadErr.message || 'Upload failed.');
        setSaving(false);
        return;
      }
      const { error: insertErr } = await filesSupabase.from('fm_files').insert({
        folder_id: currentFolderId,
        name: file.name,
        storage_key: storageKey,
        size_bytes: file.size,
        content_type: file.type || null,
      });
      if (insertErr) {
        setUploadError(insertErr.message);
        setSaving(false);
        return;
      }
      const { data: inserted } = await filesSupabase.from('fm_files').select('id').eq('storage_key', storageKey).single();
      if (inserted) {
        for (const uid of uploadAccess) {
          await filesSupabase.from('fm_file_access').insert({ file_id: inserted.id, user_id: uid });
        }
      }
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
    setShowUpload(false);
    setSaving(false);
    fetchFoldersAndFiles();
  };

  const openRenameModal = (item) => {
    setRenameTarget(item);
    setRenameValue(item.type === 'folder' ? item.data.name : item.data.name);
    setShowRename(true);
  };

  const submitRename = async (e) => {
    e.preventDefault();
    if (!renameTarget || !renameValue.trim()) return;
    setSaving(true);
    await runWakeUp();
    const table = renameTarget.type === 'folder' ? 'fm_folders' : 'fm_files';
    const { error: err } = await filesSupabase
      .from(table)
      .update({ name: renameValue.trim(), updated_at: new Date().toISOString() })
      .eq('id', renameTarget.data.id);
    setSaving(false);
    if (err) setError(err.message);
    else {
      setShowRename(false);
      setRenameTarget(null);
      fetchFoldersAndFiles();
      if (selectedItem?.data?.id === renameTarget.data.id) setSelectedItem({ ...selectedItem, data: { ...selectedItem.data, name: renameValue.trim() } });
    }
  };

  const openDeleteConfirm = (item) => {
    setDeleteTarget(item);
    setShowDelete(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    await runWakeUp();
    if (deleteTarget.type === 'file') {
      await filesSupabase.storage.from(FM_BUCKET).remove([deleteTarget.data.storage_key]);
      await filesSupabase.from('fm_files').delete().eq('id', deleteTarget.data.id);
    } else {
      await filesSupabase.from('fm_folders').delete().eq('id', deleteTarget.data.id);
    }
    setSaving(false);
    setShowDelete(false);
    setDeleteTarget(null);
    setSelectedItem(null);
    fetchFoldersAndFiles();
    fetchFavorites();
  };

  const copyFile = async (file) => {
    if (!user?.id) return;
    setSaving(true);
    setError(null);
    await runWakeUp();
    const newKey = `${user.id}/${Date.now()}_${file.name}`;
    const { error: copyErr } = await filesSupabase.storage.from(FM_BUCKET).copy(file.storage_key, newKey);
    if (copyErr) {
      setError(copyErr.message || 'Copy failed.');
      setSaving(false);
      return;
    }
    const { error: insertErr } = await filesSupabase.from('fm_files').insert({
      folder_id: currentFolderId,
      name: `Copy of ${file.name}`,
      storage_key: newKey,
      size_bytes: file.size_bytes,
      content_type: file.content_type,
    });
    setSaving(false);
    if (insertErr) setError(insertErr.message);
    else fetchFoldersAndFiles();
  };

  const toggleFavorite = async (type, id) => {
    if (!user?.id) return;
    await runWakeUp();
    const isFav = isFavorited(type, id);
    if (isFav) {
      const col = type === 'file' ? 'file_id' : 'folder_id';
      await filesSupabase.from('fm_favorites').delete().eq('user_id', user.id).eq(col, id);
    } else {
      await filesSupabase.from('fm_favorites').insert({
        user_id: user.id,
        file_id: type === 'file' ? id : null,
        folder_id: type === 'folder' ? id : null,
      });
    }
    fetchFavorites();
  };

  const openAccessModal = async (item) => {
    setAccessTarget(item);
    await runWakeUp();
    const table = item.type === 'folder' ? 'fm_folder_access' : 'fm_file_access';
    const idCol = item.type === 'folder' ? 'folder_id' : 'file_id';
    const { data } = await filesSupabase.from(table).select('user_id').eq(idCol, item.data.id);
    setAccessList(data?.map((r) => r.user_id) ?? []);
    setAccessAddUserId('');
    setShowAccess(true);
  };

  const addAccess = async () => {
    if (!accessTarget || !accessAddUserId) return;
    await runWakeUp();
    const table = accessTarget.type === 'folder' ? 'fm_folder_access' : 'fm_file_access';
    const idCol = accessTarget.type === 'folder' ? 'folder_id' : 'file_id';
    const idVal = accessTarget.data.id;
    await filesSupabase.from(table).insert({ [idCol]: idVal, user_id: accessAddUserId });
    setAccessList((prev) => [...prev, accessAddUserId]);
    setAccessAddUserId('');
  };

  const openFileDownload = async (storageKey, fileName) => {
    if (!storageKey) return;
    setError(null);
    try {
      const url = await getSignedDownloadUrl(filesSupabase, storageKey);
      if (!url) return;
      const res = await fetch(url, { mode: 'cors' });
      if (!res.ok) throw new Error(res.statusText || 'Download failed');
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = fileName || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch (err) {
      const msg = err?.message || String(err);
      setError(msg.includes('Bucket not found') ? 'Storage bucket "fm-files" not found. Create it in Supabase Dashboard → Storage → New bucket.' : msg);
    }
  };

  const downloadOfficialDoc = async () => {
    setDownloadingOfficialDoc(true);
    setError(null);
    try {
      const res = await fetch(OFFICIAL_DOC_TEMPLATE_URL);
      if (!res.ok) throw new Error('Template not found');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'official-document.docx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.message || 'Download failed.');
    } finally {
      setDownloadingOfficialDoc(false);
    }
  };

  const removeAccess = async (userId) => {
    if (!accessTarget) return;
    await runWakeUp();
    const table = accessTarget.type === 'folder' ? 'fm_folder_access' : 'fm_file_access';
    const idCol = accessTarget.type === 'folder' ? 'folder_id' : 'file_id';
    await filesSupabase.from(table).delete().eq(idCol, accessTarget.data.id).eq('user_id', userId);
    setAccessList((prev) => prev.filter((id) => id !== userId));
  };

  const getDescendantFolderIds = (allFolders, folderId) => {
    const ids = new Set([folderId]);
    let changed = true;
    while (changed) {
      changed = false;
      for (const f of allFolders) {
        if (f.parent_id && ids.has(f.parent_id) && !ids.has(f.id)) {
          ids.add(f.id);
          changed = true;
        }
      }
    }
    return ids;
  };

  const openMoveModal = async (item) => {
    setMoveTarget(item);
    setMoveDestinationId(null);
    setError(null);
    await runWakeUp();
    const { data: allFolders } = await filesSupabase.from('fm_folders').select('id, name, parent_id').order('name');
    const list = allFolders ?? [];
    const idToFolder = Object.fromEntries(list.map((f) => [f.id, f]));
    const getPath = (f) => {
      if (!f.parent_id) return f.name;
      const parent = idToFolder[f.parent_id];
      return parent ? `${getPath(parent)} / ${f.name}` : f.name;
    };
    let allowedIds = new Set(list.map((f) => f.id));
    if (item.type === 'folder') {
      const exclude = getDescendantFolderIds(list, item.data.id);
      allowedIds = new Set([...allowedIds].filter((id) => !exclude.has(id)));
    }
    const options = [{ id: null, path: 'Root' }];
    for (const f of list) {
      if (allowedIds.has(f.id)) options.push({ id: f.id, path: getPath(f) });
    }
    setMoveFolderOptions(options);
    setShowMove(true);
  };

  const confirmMove = async (e) => {
    e.preventDefault();
    if (!moveTarget) return;
    setSaving(true);
    setError(null);
    await runWakeUp();
    let err = null;
    if (moveTarget.type === 'file') {
      const res = await filesSupabase
        .from('fm_files')
        .update({ folder_id: moveDestinationId, updated_at: new Date().toISOString() })
        .eq('id', moveTarget.data.id);
      err = res.error;
    } else {
      const res = await filesSupabase
        .from('fm_folders')
        .update({ parent_id: moveDestinationId, updated_at: new Date().toISOString() })
        .eq('id', moveTarget.data.id);
      err = res.error;
    }
    setSaving(false);
    if (err) setError(err.message);
    else {
      setShowMove(false);
      setMoveTarget(null);
      setMoveDestinationId(null);
      setSelectedItem(null);
      fetchFoldersAndFiles();
      fetchFavorites();
    }
  };

  const filteredFolders = search.trim()
    ? folders.filter((f) => f.name.toLowerCase().includes(search.trim().toLowerCase()))
    : folders;
  const filteredFiles = search.trim()
    ? files.filter((f) => f.name.toLowerCase().includes(search.trim().toLowerCase()))
    : files;

  const currentTitle = favoritesView ? 'Favorites' : breadcrumb[breadcrumb.length - 1]?.name || 'Home';

  return (
    <div className="files-page">
      <aside className="files-nav">
        <div className="files-search-wrap">
          <IconSearch />
          <input
            type="search"
            className="files-search"
            placeholder="Search folder or file"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <nav className="files-nav-links">
          <button
            type="button"
            className={`files-nav-item ${!favoritesView ? 'active' : ''}`}
            onClick={() => navigateToFolder(null)}
          >
            <IconHome />
            Home
          </button>
          <button
            type="button"
            className={`files-nav-item ${favoritesView ? 'active' : ''}`}
            onClick={() => { setFavoritesView(true); setSelectedItem(null); }}
          >
            <IconStar />
            Favorites
          </button>
        </nav>
      </aside>

      <section className="files-list-pane">
        <div className="files-list-header">
          <h2 className="files-list-title">
            {breadcrumb.length > 1 ? (
              <span className="files-breadcrumb">
                {breadcrumb.map((b, i) => (
                  <React.Fragment key={b.id ?? 'root'}>
                    <button type="button" className="files-breadcrumb-item" onClick={() => navigateBreadcrumb(b)}>
                      {b.name}
                    </button>
                    {i < breadcrumb.length - 1 && <span className="files-breadcrumb-sep">/</span>}
                  </React.Fragment>
                ))}
              </span>
            ) : (
              <>
                {favoritesView ? <IconStar /> : <IconHome />}
                {currentTitle}
              </>
            )}
          </h2>
          {!favoritesView && (
            <div className="files-list-actions">
              <button
                type="button"
                className="files-action-btn files-action-btn-word"
                onClick={downloadOfficialDoc}
                disabled={downloadingOfficialDoc}
              >
                <IconDownload />
                {downloadingOfficialDoc ? 'Preparing…' : 'Download official doc'}
              </button>
              <button type="button" className="files-action-btn" onClick={openNewFolderModal}>
                <IconFolder />
                <IconPlus className="files-btn-plus" />
                New folder
              </button>
              <button type="button" className="files-action-btn" onClick={openUploadModal}>
                <IconNote />
                <IconPlus className="files-btn-plus" />
                Add files
              </button>
            </div>
          )}
        </div>

        {error && (
          <div className="files-error" role="alert">
            {error}
          </div>
        )}

        {loading ? (
          <p className="files-loading">Loading…</p>
        ) : favoritesView ? (
          <ul className="files-list">
            {favorites.map((fav) => (
              <li key={fav.id}>
                {fav.folder_id ? (
                  (() => {
                    const folder = favoritedFolders.find((f) => f.id === fav.folder_id) || { id: fav.folder_id, name: 'Folder' };
                    return (
                      <button
                        type="button"
                        className="files-item"
                        onClick={() => { setFavoritesView(false); navigateToFolder(folder); }}
                      >
                        <span className="files-item-icon files-folder-icon"><IconFolder /></span>
                        <span className="files-item-name">{folder.name}</span>
                        <span className="files-item-meta">Folder</span>
                      </button>
                    );
                  })()
                ) : (
                  (() => {
                    const file = favoritedFiles.find((f) => f.id === fav.file_id) || { id: fav.file_id, name: 'File', storage_key: '' };
                    return (
                      <button
                        type="button"
                        className="files-item files-item-link"
                        onClick={() => file.storage_key && openFileDownload(file.storage_key, file.name)}
                        disabled={!file.storage_key}
                      >
                        <span className="files-item-icon"><IconNote /></span>
                        <span className="files-item-name">{file.name}</span>
                        <span className="files-item-meta">{formatSize(file.size_bytes)}</span>
                      </button>
                    );
                  })()
                )}
              </li>
            ))}
            {favorites.length === 0 && <li className="files-empty">No favorites yet.</li>}
          </ul>
        ) : (
          <ul className="files-list">
            {filteredFolders.map((folder) => (
              <li key={folder.id}>
                <button
                  type="button"
                  className={`files-item ${selectedItem?.type === 'folder' && selectedItem?.data?.id === folder.id ? 'selected' : ''}`}
                  onClick={() => { setSelectedItem({ type: 'folder', data: folder }); }}
                  onDoubleClick={() => navigateToFolder(folder)}
                >
                  <span className="files-item-icon files-folder-icon"><IconFolder /></span>
                  <span className="files-item-name">{folder.name}</span>
                  <span className="files-item-meta">Folder</span>
                </button>
              </li>
            ))}
            {filteredFiles.map((file) => (
              <li key={file.id}>
                <button
                  type="button"
                  className={`files-item ${selectedItem?.type === 'file' && selectedItem?.data?.id === file.id ? 'selected' : ''}`}
                  onClick={() => setSelectedItem({ type: 'file', data: file })}
                >
                  <span className="files-item-icon"><IconNote /></span>
                  <span className="files-item-name">{file.name}</span>
                  <span className="files-item-meta">{formatSize(file.size_bytes)}</span>
                </button>
              </li>
            ))}
            {filteredFolders.length === 0 && filteredFiles.length === 0 && !loading && (
              <li className="files-empty">No files or folders here.</li>
            )}
          </ul>
        )}
      </section>

      {detailsOpen && selectedItem && (
        <aside className="files-details">
          <div className="files-details-header">
            <h3 className="files-details-title">{selectedItem.data.name}</h3>
            <button type="button" className="files-details-close" onClick={() => setDetailsOpen(false)} aria-label="Close details">
              <IconClose />
            </button>
          </div>
          <div className="files-details-body">
            <p className="files-details-meta">
              {selectedItem.type === 'folder' ? 'Folder' : formatSize(selectedItem.data.size_bytes)}
            </p>
            <p className="files-details-meta">Created {formatDate(selectedItem.data.created_at)}</p>
            <div className="files-details-actions">
              <button type="button" className="files-detail-btn" onClick={() => openRenameModal(selectedItem)}>
                <IconEdit /> Rename
              </button>
              {selectedItem.type === 'file' && (
                <>
                  <button
                    type="button"
                    className="files-detail-btn"
                    onClick={() => openFileDownload(selectedItem.data.storage_key, selectedItem.data.name)}
                  >
                    <IconDownload /> Download
                  </button>
                  <button type="button" className="files-detail-btn" onClick={() => copyFile(selectedItem.data)} disabled={saving}>
                    <IconCopy /> Copy
                  </button>
                </>
              )}
              {selectedItem.type === 'folder' && (
                <button type="button" className="files-detail-btn" onClick={() => navigateToFolder(selectedItem.data)}>
                  Open
                </button>
              )}
              <button type="button" className="files-detail-btn" onClick={() => openMoveModal(selectedItem)}>
                <IconMove /> Move to…
              </button>
              <button type="button" className="files-detail-btn" onClick={() => toggleFavorite(selectedItem.type, selectedItem.data.id)}>
                <IconStar /> {isFavorited(selectedItem.type, selectedItem.data.id) ? 'Unfavorite' : 'Favorite'}
              </button>
              <button type="button" className="files-detail-btn" onClick={() => openAccessModal(selectedItem)}>
                <IconLock /> Access
              </button>
              <button type="button" className="files-detail-btn files-detail-btn-danger" onClick={() => openDeleteConfirm(selectedItem)}>
                <IconTrash /> Delete
              </button>
            </div>
          </div>
        </aside>
      )}

      {!detailsOpen && (
        <button type="button" className="files-details-toggle" onClick={() => setDetailsOpen(true)} title="Show details">
          Details
        </button>
      )}

      {/* New folder modal */}
      {showNewFolder && (
        <div className="files-modal-backdrop" onClick={() => setShowNewFolder(false)}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="files-modal-title">New folder</h3>
            <form onSubmit={createFolder}>
              <div className="files-form-group">
                <label>Folder name</label>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Folder name"
                  required
                  className="files-form-input"
                />
              </div>
              <div className="files-form-group">
                <label>Who can access (optional)</label>
                <select
                  multiple
                  size={4}
                  className="files-form-select"
                  value={newFolderAccess}
                  onChange={(e) => setNewFolderAccess(Array.from(e.target.selectedOptions, (o) => o.value))}
                >
                  {profiles.filter((p) => p.id !== user?.id).map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name || p.id}</option>
                  ))}
                </select>
              </div>
              <div className="files-modal-footer">
                <button type="button" className="files-btn files-btn-secondary" onClick={() => setShowNewFolder(false)}>Cancel</button>
                <button type="submit" className="files-btn files-btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="files-modal-backdrop" onClick={() => !saving && setShowUpload(false)}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="files-modal-title">Add files (max 5 MB each)</h3>
            <form onSubmit={(e) => e.preventDefault()}>
              <div className="files-form-group">
                <label>Select files</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  className="files-form-input"
                  onChange={handleFileSelect}
                  accept="*/*"
                  disabled={saving}
                />
              </div>
              <div className="files-form-group">
                <label>Who can access (optional)</label>
                <select
                  multiple
                  size={3}
                  className="files-form-select"
                  value={uploadAccess}
                  onChange={(e) => setUploadAccess(Array.from(e.target.selectedOptions, (o) => o.value))}
                >
                  {profiles.filter((p) => p.id !== user?.id).map((p) => (
                    <option key={p.id} value={p.id}>{p.full_name || p.id}</option>
                  ))}
                </select>
              </div>
              {uploadError && <p className="files-form-error">{uploadError}</p>}
              {saving && <p className="files-form-muted">Uploading…</p>}
              <div className="files-modal-footer">
                <button type="button" className="files-btn files-btn-secondary" onClick={() => setShowUpload(false)} disabled={saving}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Rename modal */}
      {showRename && renameTarget && (
        <div className="files-modal-backdrop" onClick={() => setShowRename(false)}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="files-modal-title">Rename {renameTarget.type}</h3>
            <form onSubmit={submitRename}>
              <div className="files-form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  required
                  className="files-form-input"
                />
              </div>
              <div className="files-modal-footer">
                <button type="button" className="files-btn files-btn-secondary" onClick={() => setShowRename(false)}>Cancel</button>
                <button type="submit" className="files-btn files-btn-primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {showDelete && deleteTarget && (
        <div className="files-modal-backdrop" onClick={() => setShowDelete(false)}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="files-modal-title">Delete {deleteTarget.type}?</h3>
            <p className="files-confirm-p">Are you sure you want to delete &quot;{deleteTarget.data.name}&quot;? This cannot be undone.</p>
            <div className="files-modal-footer">
              <button type="button" className="files-btn files-btn-secondary" onClick={() => setShowDelete(false)}>Cancel</button>
              <button type="button" className="files-btn files-btn-danger" onClick={confirmDelete} disabled={saving}>{saving ? 'Deleting…' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Access modal */}
      {showAccess && accessTarget && (
        <div className="files-modal-backdrop" onClick={() => setShowAccess(false)}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="files-modal-title">Access: {accessTarget.data.name}</h3>
            <div className="files-form-group">
              <label>People with access</label>
              <ul className="files-access-list">
                {accessList.map((uid) => (
                  <li key={uid}>
                    <span>{profiles.find((p) => p.id === uid)?.full_name || uid}</span>
                    <button type="button" className="files-access-remove" onClick={() => removeAccess(uid)}>Remove</button>
                  </li>
                ))}
              </ul>
            </div>
            <div className="files-form-group">
              <label>Add person</label>
              <select
                value={accessAddUserId}
                onChange={(e) => setAccessAddUserId(e.target.value)}
                className="files-form-input"
              >
                <option value="">— Select —</option>
                {profiles.filter((p) => p.id !== user?.id && !accessList.includes(p.id)).map((p) => (
                  <option key={p.id} value={p.id}>{p.full_name || p.id}</option>
                ))}
              </select>
              <button type="button" className="files-btn files-btn-primary files-btn-sm" onClick={addAccess} disabled={!accessAddUserId}>Add</button>
            </div>
            <div className="files-modal-footer">
              <button type="button" className="files-btn files-btn-secondary" onClick={() => setShowAccess(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Move modal */}
      {showMove && moveTarget && (
        <div className="files-modal-backdrop" onClick={() => !saving && setShowMove(false)}>
          <div className="files-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="files-modal-title">Move {moveTarget.data.name} to</h3>
            <form onSubmit={confirmMove}>
              <div className="files-form-group">
                <label>Destination folder</label>
                <select
                  value={moveDestinationId ?? ''}
                  onChange={(e) => setMoveDestinationId(e.target.value === '' ? null : e.target.value)}
                  className="files-form-input"
                  required
                >
                  <option value="">— Select folder —</option>
                  {moveFolderOptions.map((opt) => (
                    <option key={opt.id ?? 'root'} value={opt.id ?? ''}>
                      {opt.path}
                    </option>
                  ))}
                </select>
              </div>
              {error && <p className="files-form-error">{error}</p>}
              <div className="files-modal-footer">
                <button type="button" className="files-btn files-btn-secondary" onClick={() => setShowMove(false)} disabled={saving}>Cancel</button>
                <button type="submit" className="files-btn files-btn-primary" disabled={saving}>{saving ? 'Moving…' : 'Move'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
